import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Checkbox } from "antd";
import {
  isAccessTokenExpired,
  refreshAccessToken,
  startSession,
} from "../../../services/tokenRefresh";
import AuthService from "../../../services/authentication.service";
import "./styles.scss";
import { fileService } from "../../../services/fileService.service";
import { resilientPost } from "../../../services/authRequest";
import {
  ERROR_KIND,
  backendMessage,
  isOffline,
} from "../../../services/requestErrors";

const PENDING_CODE_MESSAGE =
  "User already has a pending confirmation code. Please check your email or resend code";

// What to show under the Sign in button while a slow sign-in is in progress.
const progressText = (progress) => {
  if (!progress) return null;
  const { attempt, maxAttempts, slow, waitingToRetry } = progress;
  const retrying = waitingToRetry || attempt > 1;
  if (retrying) {
    const next = waitingToRetry ? attempt + 1 : attempt;
    return `${
      slow ? "Slow connection detected" : "Connection problem"
    }, retrying (attempt ${next} of ${maxAttempts})…`;
  }
  return slow ? "Slow connection detected, still trying…" : null;
};

const LoginBox = ({ isShare }) => {
  const [form] = Form.useForm();
  const [loginLoading, setLoginLoading] = useState(false);
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [progress, setProgress] = useState(null);
  const [offline, setOffline] = useState(isOffline());
  const abortRef = useRef(null);

  const navigate = useNavigate();

  // Live connection state: warn while offline, and drop a stale "no
  // internet" error once the connection is back.
  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => {
      setOffline(false);
      setLoginError((current) =>
        current?.kind === ERROR_KIND.OFFLINE ||
        current?.kind === ERROR_KIND.NO_INTERNET
          ? null
          : current
      );
    };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    localStorage.removeItem("email");
    const token = AuthService.getAccessToken();
    const clearStoredUser = () => {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("tokenExpiresAt");
      localStorage.removeItem("refreshTokenExpiresAt");
      localStorage.removeItem("userEmail");
      localStorage.removeItem("email");
      localStorage.removeItem("username");
      localStorage.removeItem("role");
      localStorage.removeItem("userID");
    };
    if (token && !isAccessTokenExpired(token)) {
      navigate("/dashboard/contracts");
    } else if (token && AuthService.getRefreshToken()) {
      // The access token only lasts 2 hours; a live refresh token still keeps
      // the user signed in.
      refreshAccessToken(token)
        .then(() => navigate("/dashboard/contracts"))
        .catch((error) => error?.sessionEnded && clearStoredUser());
    } else {
      clearStoredUser();
    }
  }, [navigate]);

  const saveImageAsBase64 = async (imageUrl, localStorageKey) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      const reader = new FileReader();
      reader.readAsDataURL(blob);
      return new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64data = reader.result;
          localStorage.setItem(localStorageKey, base64data);
          resolve(base64data);
        };
        reader.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error saving image:", error);
      throw error;
    }
  };

  const setProfilePicture = async (fileKey) => {
    const url = `/FileData/getFileByFileKey?fileKey=${fileKey}`;
    await fileService.get(
      url,
      (response) => {
        const profilePictureUrl = response?.data?.data;
        saveImageAsBase64(profilePictureUrl, "profilePicture");
      },
      (error) => {
        // console.log(error);
      }
    );
  };

  const handleSignUp = () => {
  setSignUpLoading(true);
  // navigate("/sign-up");
};

  const handleLogin = async (values) => {
    const { email, password } = values;
    if (loginLoading) return;

    setLoginLoading(true);
    setLoginError(null);
    setProgress(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await resilientPost(
        "/api/Account/login",
        { email, password },
        { signal: controller.signal, onProgress: setProgress }
      );

      if (!response?.data?.confirmationCodeSent) {
        startSession(response.data);
        localStorage.setItem("userEmail", response.data.email);
        localStorage.setItem("username", response.data.fullName);

        localStorage.setItem("role", response.data.roles[0]?.roleName);
        localStorage.setItem("roleId", response.data.roles[0]?.id);
        localStorage.setItem("userID", response.data.id);
        localStorage.setItem(
          "profilePictureFileKey",
          response?.data?.profilePictureUrl || ""
        );
        response?.data?.profilePictureUrl &&
          setProfilePicture(response?.data?.profilePictureUrl);
        navigate("/dashboard/contracts");
      } else {
        navigate("/confirmationCode", { state: { email } });
      }
    } catch (failure) {
      if (
        backendMessage(failure?.error?.response?.data) === PENDING_CODE_MESSAGE
      ) {
        navigate("/confirmationCode", { state: { email } });
      } else if (failure?.kind !== ERROR_KIND.CANCELED) {
        setLoginError(
          failure?.kind
            ? failure
            : {
                kind: ERROR_KIND.REJECTED,
                title: "Sign-in failed",
                message: "Something went wrong. Please try again.",
                retryable: true,
              }
        );
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setLoginLoading(false);
      setProgress(null);
    }
  };

  const cancelLogin = () => abortRef.current?.abort();

  // Retry with what is already in the form; nothing has to be re-typed.
  const retryLogin = () => form.submit();

  const statusText = loginLoading ? progressText(progress) : null;
  const showOfflineBanner =
    offline && loginError?.kind !== ERROR_KIND.OFFLINE && !loginLoading;

  const handleForgotPassword = () => {
    navigate("/forgotPassword");
  };

  return (
    <>
      <div className="form-wrapper">
        <p>Welcome back!</p>
        {isShare ? (
          <h2>
            <b>TPM</b>ready
          </h2>
        ) : (
          <h2>TPMready Management System</h2>
        )}
        <Form
          form={form}
          onFinish={handleLogin}
          layout="vertical"
          style={{ width: "100%" }}
        >
          {showOfflineBanner && (
            <div className="loginNotice loginNotice--warning" role="status">
              <strong>You're offline</strong>
              <span>
                Connect to Wi-Fi or mobile data to sign in. This message clears
                when the connection is back.
              </span>
            </div>
          )}
          {loginError && !loginLoading && (
            <div className="loginNotice loginNotice--error" role="alert">
              <strong>{loginError.title}</strong>
              <span>{loginError.message}</span>
              {loginError.retryable && (
                <button
                  type="button"
                  className="loginNotice__action"
                  onClick={retryLogin}
                >
                  Try again
                </button>
              )}
            </div>
          )}
          <Form.Item
            label="Email address"
            name="email"
            rules={[{ required: true, message: "Please input your email!" }]}
          >
            <Input type="email" />
          </Form.Item>
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: "Please input your password!" }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item name="rememberMe" valuePropName="checked">
            <Checkbox>Remember me</Checkbox>
          </Form.Item>
          <p
            className="forgotPassword text-start"
            onClick={handleForgotPassword}
            style={{
              cursor: "pointer",
              color: "blue",
              textDecoration: "underline",
              width: "100%",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            Forgot your password?
          </p>
          <Form.Item style={{ width: "100%" }}>
            <Button
              className="addButtonTPM"
              htmlType="submit"
              loading={loginLoading}
              block
            >
              {loginLoading ? "Signing in…" : "Sign in"}
            </Button>
            {statusText && (
              <div className="loginStatus" role="status" aria-live="polite">
                <span>{statusText}</span>
                <button
                  type="button"
                  className="loginStatus__cancel"
                  onClick={cancelLogin}
                >
                  Cancel
                </button>
              </div>
            )}
          </Form.Item>
            <Form.Item style={{ width: "100%" }}>
            <Button
              className="inviteButtonTPM"
              href="/sign-up"
              onClick={handleSignUp}
              loading={signUpLoading}
              block
            >
              Sign up
            </Button>
          </Form.Item>
        </Form>
      </div>
    </>
  );
};

export default LoginBox;
